import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import api from '../api';
import DEFAULT_SITE_SETTINGS, { mergeSiteSettings } from '../siteDefaults';
import './Home.css';

function SmartLink({ href, className, children, ...props }) {
  if (href?.startsWith('/') && !href.startsWith('//')) {
    return <Link className={className} to={href} {...props}>{children}</Link>;
  }
  return <a className={className} href={href || '#'} {...props}>{children}</a>;
}

function BrandMark({ brand, footer = false }) {
  return (
    <span className={`home-brand${footer ? ' home-footer-brand' : ''}`}>
      {brand.logoUrl ? (
        <img className="home-brand-image" src={brand.logoUrl} alt={`${brand.name} logo`} />
      ) : (
        <Logo size={42} />
      )}
      <span>
        <strong>{brand.name}</strong>
        <small>{brand.descriptor}</small>
      </span>
    </span>
  );
}

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SITE_SETTINGS);

  useEffect(() => {
    let active = true;
    api.get('/site-settings/public')
      .then(response => {
        if (active) setSettings(mergeSiteSettings(DEFAULT_SITE_SETTINGS, response.data));
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    document.title = `${settings.brand.name} | ${settings.brand.descriptor}`;
  }, [settings.brand.name, settings.brand.descriptor]);

  const closeMenu = () => setMenuOpen(false);
  const { brand, theme, navigation, hero, highlights, experience, rooms, amenities, promise, contact, cta, footer } = settings;
  const phoneHref = `tel:${contact.phone.replace(/[^+\d]/g, '')}`;
  const themeStyle = {
    '--home-navy': theme.primary,
    '--home-navy-soft': theme.primarySoft,
    '--home-paper': theme.background,
    '--home-cream': theme.surface,
    '--home-gold': theme.accent,
    '--home-muted': theme.muted
  };

  return (
    <div className="home-page" style={themeStyle}>
      <a className="home-skip-link" href="#home-main">Skip to content</a>

      {navigation.headerVisible && <header className="home-header">
        <a href="#top" aria-label={`${brand.name} home`} onClick={closeMenu}>
          <BrandMark brand={brand} />
        </a>

        <button className="home-menu-button" type="button" aria-label="Toggle navigation" aria-expanded={menuOpen} aria-controls="home-navigation" onClick={() => setMenuOpen(!menuOpen)}>
          <span /><span />
        </button>

        <nav id="home-navigation" className={`home-nav${menuOpen ? ' is-open' : ''}`}>
          {navigation.experienceVisible && experience.visible && <a href="#experience" onClick={closeMenu}>{navigation.experienceLabel}</a>}
          {navigation.roomsVisible && rooms.visible && <a href="#rooms" onClick={closeMenu}>{navigation.roomsLabel}</a>}
          {navigation.amenitiesVisible && amenities.visible && <a href="#amenities" onClick={closeMenu}>{navigation.amenitiesLabel}</a>}
          {navigation.contactVisible && contact.visible && <a href="#contact" onClick={closeMenu}>{navigation.contactLabel}</a>}
          {navigation.loginVisible && <Link className="home-nav-login" to="/login" onClick={closeMenu}>{navigation.loginLabel}</Link>}
        </nav>
      </header>}

      <main id="home-main">
        {hero.visible && <section className="home-hero" id="top">
          <div className="home-hero-copy">
            <p className="home-eyebrow">{hero.eyebrow}</p>
            <h1>{hero.title}<br /><em>{hero.accentTitle}</em></h1>
            <p className="home-hero-text">{hero.copy}</p>
            <div className="home-hero-actions">
              {hero.primaryButtonVisible && <SmartLink className="home-button home-button-primary" href={hero.primaryButtonLink}>{hero.primaryButtonLabel}</SmartLink>}
              {hero.secondaryButtonVisible && <SmartLink className="home-text-link" href={hero.secondaryButtonLink}>{hero.secondaryButtonLabel} <span aria-hidden="true">&rarr;</span></SmartLink>}
            </div>
            <div className="home-essentials" aria-label="Residence essentials">
              {hero.essentials.map((item, index) => <span key={`${item}-${index}`}>{item}</span>)}
            </div>
          </div>

          <div className="home-hero-visual" aria-label={`${brand.name} residence`}>
            <img src={hero.image} alt={hero.imageAlt} />
            <div className="home-availability-card">
              <span className="home-status-dot" />
              <div><strong>{hero.cardTitle}</strong><small>{hero.cardNote}</small></div>
            </div>
            <div className="home-hero-caption">{hero.caption}</div>
          </div>
        </section>}

        {settings.highlightsVisible && (
          <section className="home-highlights" aria-label={`${brand.name} at a glance`}>
            {highlights.map((item, index) => (
              <div className="home-highlight" key={`${item.label}-${index}`}><strong>{item.value}</strong><span>{item.label}</span></div>
            ))}
          </section>
        )}

        {experience.visible && (
          <section className="home-experience" id="experience">
            <div className="home-section-heading">
              <p className="home-eyebrow">{experience.eyebrow}</p>
              <h2>{experience.title}<br />{experience.accentTitle}</h2>
            </div>
            <div className="home-experience-copy"><p>{experience.copy}</p></div>
            <div className="home-experience-grid">
              {experience.items.map((item, index) => (
                <article className="home-experience-item" key={`${item.number}-${index}`}>
                  <span>{item.number}</span><h3>{item.title}</h3><p>{item.copy}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {rooms.visible && (
          <section className="home-rooms" id="rooms">
            <div className="home-rooms-header">
              <div><p className="home-eyebrow">{rooms.eyebrow}</p><h2>{rooms.title}<br />{rooms.accentTitle}</h2></div>
              <p>{rooms.copy}</p>
            </div>
            <div className="home-room-grid">
              {rooms.items.map((room, index) => (
                <article className="home-room-card" key={`${room.name}-${index}`}>
                  <div className="home-room-image"><img src={room.image} alt={room.alt} loading="lazy" /><span>0{index + 1}</span></div>
                  <div className="home-room-details"><h3>{room.name}</h3><p>{room.note}</p></div>
                </article>
              ))}
            </div>
          </section>
        )}

        {amenities.visible && (
          <section className="home-amenities" id="amenities">
            <div className="home-amenities-image"><img src={amenities.image} alt={amenities.imageAlt} loading="lazy" /></div>
            <div className="home-amenities-content">
              <p className="home-eyebrow">{amenities.eyebrow}</p>
              <h2>{amenities.title}<br /><em>{amenities.accentTitle}</em></h2>
              <div className="home-amenity-list">
                {amenities.items.map((item, index) => <div key={`${item.title}-${index}`}><strong>{item.title}</strong><span>{item.copy}</span></div>)}
              </div>
            </div>
          </section>
        )}

        {promise.visible && (
          <section className="home-testimonial">
            <p className="home-quote-mark" aria-hidden="true">{promise.monogram}</p>
            <h2>{promise.title}</h2>
            <p className="home-quote-author">{promise.label}</p>
          </section>
        )}

        {contact.visible && (
          <section className="home-contact" id="contact">
            <div className="home-contact-content">
              <p className="home-eyebrow">{contact.eyebrow}</p>
              <h2>{contact.title}<br /><em>{contact.accentTitle}</em></h2>
              <p className="home-contact-intro">{contact.copy}</p>
              <div className="home-contact-list">
                <div><span>Address</span><a href={contact.mapLink} target="_blank" rel="noreferrer">{contact.address}</a></div>
                <div><span>Phone</span><a href={phoneHref}>{contact.phone}</a></div>
                <div><span>Google Maps</span><a className="home-map-link" href={contact.mapLink} target="_blank" rel="noreferrer">Open map &amp; directions <span aria-hidden="true">&rarr;</span></a></div>
              </div>
              <p className="home-map-listing">Listed on Google Maps as “{contact.listingName}”</p>
            </div>
            <div className="home-map-frame">
              <iframe title={`${contact.listingName} location`} src={contact.mapEmbedUrl} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              <a href={contact.mapLink} target="_blank" rel="noreferrer"><strong>{contact.listingName}</strong><span>Open in Google Maps &rarr;</span></a>
            </div>
          </section>
        )}

        {cta.visible && (
          <section className="home-cta">
            <p className="home-eyebrow">{cta.eyebrow}</p>
            <h2>{cta.title}<br /><em>{cta.accentTitle}</em></h2>
            <p>{cta.copy}</p>
            <div className="home-cta-actions">
              {cta.primaryButtonVisible && <SmartLink className="home-button home-button-light" href={cta.primaryButtonLink}>{cta.primaryButtonLabel}</SmartLink>}
              {cta.secondaryButtonVisible && <SmartLink className="home-text-link home-text-link-light" href={cta.secondaryButtonLink}>{cta.secondaryButtonLabel} <span aria-hidden="true">&rarr;</span></SmartLink>}
            </div>
          </section>
        )}
      </main>

      {footer.visible && <footer className="home-footer">
        <a href="#top" aria-label="Back to top"><BrandMark brand={brand} footer /></a>
        <p>{footer.tagline}</p>
        <div className="home-footer-links">
          {experience.visible && <a href="#experience">{navigation.experienceLabel}</a>}
          {rooms.visible && <a href="#rooms">{navigation.roomsLabel}</a>}
          {amenities.visible && <a href="#amenities">{navigation.amenitiesLabel}</a>}
          {contact.visible && <a href="#contact">{navigation.contactLabel}</a>}
          {navigation.loginVisible && <Link to="/login">{navigation.loginLabel}</Link>}
        </div>
        <small>&copy; {new Date().getFullYear()} {footer.copyright}</small>
      </footer>}
    </div>
  );
}
